import { HttpService, IHttpService } from '../../common/api/httpService';
import { RequestResultValue } from '../../common/api/requestResult';
import {
    InventoryLoadSourceMetrics,
    InventorySyncOverview,
    InventorySyncSettingsResponse,
    ProxyStatus,
    RateLimitSettings,
    ReloadProxiesResult,
    ServiceMetricsSnapshot,
} from '../../models/inventorySync';
import { parseSaveError } from './inventorySyncSettings';
import JSONbig from 'json-bigint';

const JSONbigNative = JSONbig({ useNativeBigInt: true });

async function parseJsonResponse<T>(response: Response): Promise<T | undefined> {
    const text = await response.text();
    if (!text) {
        return undefined;
    }
    return JSONbigNative.parse(text) as T;
}

export async function getProxyStatus(): Promise<RequestResultValue<ProxyStatus[]>> {
    try {
        const response = await HttpService.getBSInstance().requestGetAsync('v1/inventory-sync/proxies');
        const body = await parseJsonResponse<ProxyStatus[]>(response);
        if (response.status >= 200 && response.status < 300 && body) {
            return new RequestResultValue<ProxyStatus[]>(true, body);
        }
        return new RequestResultValue<ProxyStatus[]>(false);
    } catch {
        return new RequestResultValue<ProxyStatus[]>(false);
    }
}

async function fetchJson<T>(http: IHttpService, path: string): Promise<T | undefined> {
    try {
        const response = await http.requestGetAsync(path);
        const body = await parseJsonResponse<T>(response);
        if (response.status >= 200 && response.status < 300 && body) {
            return body;
        }
        return undefined;
    } catch {
        return undefined;
    }
}

/** Процент с округлением до 0.1, как в ServiceMetricsCollector на бэкенде */
function percent(part: number, total: number): number {
    return total > 0 ? Math.round(part / total * 1000) / 10 : 0;
}

const EMPTY_LOAD_SOURCE: InventoryLoadSourceMetrics = {
    publicLinkBatchCount: 0,
    steamApisBatchCount: 0,
    botOwnBatchCount: 0,
    botTradeLinkBatchCount: 0,
    totalBatchCount: 0,
    publicLinkBatchPercent: 0,
    steamApisBatchPercent: 0,
    botOwnBatchPercent: 0,
    botTradeLinkBatchPercent: 0,
};

/**
 * Объединяет метрики нескольких сервисов в один снимок.
 * Счетчики, очередь, воркеры и лимиты параллелизма суммируются, проценты пересчитываются из суммарных счетчиков.
 * Среднее ожидание взвешивается по числу запросов (точное число измерений ожидания бэкенд не отдает).
 */
export function mergeServiceMetrics(snapshots: ServiceMetricsSnapshot[]): ServiceMetricsSnapshot {
    const sum = (selector: (m: ServiceMetricsSnapshot) => number) =>
        snapshots.reduce((acc, m) => acc + (selector(m) ?? 0), 0);
    const sumLoad = (selector: (l: InventoryLoadSourceMetrics) => number) =>
        snapshots.reduce((acc, m) => acc + (selector(m.inventoryLoadBySource ?? EMPTY_LOAD_SOURCE) ?? 0), 0);

    const totalRequests = sum(m => m.totalRequests);
    const successCount = sum(m => m.successCount);
    const rateLimitCount = sum(m => m.rateLimitCount);
    const httpAttemptCount = sum(m => m.httpAttemptCount);
    const requestsWith429Count = sum(m => m.requestsWith429Count);

    const avgWaitTimeMs = totalRequests > 0
        ? sum(m => m.avgWaitTimeMs * m.totalRequests) / totalRequests
        : (snapshots.length > 0 ? sum(m => m.avgWaitTimeMs) / snapshots.length : 0);

    const publicLinkBatchCount = sumLoad(l => l.publicLinkBatchCount);
    const steamApisBatchCount = sumLoad(l => l.steamApisBatchCount);
    const botOwnBatchCount = sumLoad(l => l.botOwnBatchCount);
    const botTradeLinkBatchCount = sumLoad(l => l.botTradeLinkBatchCount);
    const totalBatchCount = sumLoad(l => l.totalBatchCount);

    return {
        totalRequests,
        successCount,
        errorCount: sum(m => m.errorCount),
        rateLimitCount,
        httpAttemptCount,
        requestsWith429Count,
        successPercent: percent(successCount, totalRequests),
        rateLimitPercent: percent(rateLimitCount, httpAttemptCount),
        requestsWith429Percent: percent(requestsWith429Count, totalRequests),
        queueDepth: sum(m => m.queueDepth),
        activeWorkers: sum(m => m.activeWorkers),
        currentConcurrencyLimit: sum(m => m.currentConcurrencyLimit),
        maxConcurrencyLimit: sum(m => m.maxConcurrencyLimit),
        avgWaitTimeMs: Math.round(avgWaitTimeMs * 10) / 10,
        queueRejectedCount: sum(m => m.queueRejectedCount),
        queueTimeoutCount: sum(m => m.queueTimeoutCount),
        proxyBudgetExhaustedCount: sum(m => m.proxyBudgetExhaustedCount),
        inventoryLoadBySource: {
            publicLinkBatchCount,
            steamApisBatchCount,
            botOwnBatchCount,
            botTradeLinkBatchCount,
            totalBatchCount,
            publicLinkBatchPercent: percent(publicLinkBatchCount, totalBatchCount),
            steamApisBatchPercent: percent(steamApisBatchCount, totalBatchCount),
            botOwnBatchPercent: percent(botOwnBatchCount, totalBatchCount),
            botTradeLinkBatchPercent: percent(botTradeLinkBatchCount, totalBatchCount),
        },
    };
}

export interface InventorySyncStatisticsResult {
    /** Сводка по минутам из общего хранилища метрик (все процессы); undefined — сводка недоступна */
    overview?: InventorySyncOverview;
    /** Объединенные счетчики процессов с момента старта; undefined — ни один сервис не ответил */
    metrics?: ServiceMetricsSnapshot;
    /** Источники, которые загрузить не удалось */
    failedSources: string[];
}

/** Источник сводки в failedSources */
export const OVERVIEW_SOURCE = 'BackgroundService (сводка по минутам)';

/**
 * Сводка синка за окно (BackgroundService /overview) и счетчики процессов (BackgroundService и Bots /metrics).
 * Успех, если ответил хотя бы один источник; недоступные перечислены в failedSources.
 */
export async function getInventorySyncStatistics(minutes: number): Promise<RequestResultValue<InventorySyncStatisticsResult>> {
    const bs = HttpService.getBSInstance();
    const metricSources = [
        { name: 'BackgroundService', http: bs, path: 'v1/inventory-sync/metrics' },
        { name: 'Bots', http: HttpService.getBotInstance(), path: 'api/inventory-sync/metrics' },
    ];
    const [overview, ...metricResults] = await Promise.all([
        fetchJson<InventorySyncOverview>(bs, `v1/inventory-sync/overview?minutes=${minutes}`),
        ...metricSources.map(s => fetchJson<ServiceMetricsSnapshot>(s.http, s.path)),
    ]) as [InventorySyncOverview | undefined, ...(ServiceMetricsSnapshot | undefined)[]];

    const loaded = metricResults.filter((m): m is ServiceMetricsSnapshot => m !== undefined);
    if (!overview && loaded.length === 0) {
        return new RequestResultValue<InventorySyncStatisticsResult>(false);
    }
    return new RequestResultValue<InventorySyncStatisticsResult>(true, {
        overview,
        metrics: loaded.length > 0 ? mergeServiceMetrics(loaded) : undefined,
        failedSources: [
            ...(overview ? [] : [OVERVIEW_SOURCE]),
            ...metricSources.filter((_, i) => metricResults[i] === undefined).map(s => s.name),
        ],
    });
}

export async function getInventorySyncSettings(): Promise<RequestResultValue<InventorySyncSettingsResponse>> {
    const body = await fetchJson<InventorySyncSettingsResponse>(HttpService.getBSInstance(), 'v1/inventory-sync/settings');
    return new RequestResultValue<InventorySyncSettingsResponse>(body !== undefined, body);
}

export type SaveSettingsResult =
    | { ok: true; settings: RateLimitSettings }
    | { ok: false; errors: string[] };

/** PUT лимитов: 200 — применённые настройки; 400 — ошибки валидации; 503 — Redis недоступен/не настроен */
export async function updateInventorySyncSettings(settings: RateLimitSettings): Promise<SaveSettingsResult> {
    try {
        const response = await HttpService.getBSInstance()
            .requestPutAsync('v1/inventory-sync/settings', JSON.stringify(settings));
        const text = await response.text();
        if (response.status >= 200 && response.status < 300) {
            return { ok: true, settings: text ? JSON.parse(text) as RateLimitSettings : settings };
        }
        return { ok: false, errors: parseSaveError(response.status, text) };
    } catch {
        return { ok: false, errors: ['Ошибка сети — сервис BackgroundService недоступен'] };
    }
}

export async function reloadInventorySyncProxies(): Promise<RequestResultValue<ReloadProxiesResult>> {
    try {
        const response = await HttpService.getBSInstance().requestPostAsync('v1/inventory-sync/proxies/reload', '{}');
        const body = await parseJsonResponse<ReloadProxiesResult>(response);
        if (response.status >= 200 && response.status < 300 && body) {
            return new RequestResultValue<ReloadProxiesResult>(true, body);
        }
        return new RequestResultValue<ReloadProxiesResult>(false, body);
    } catch (e: any) {
        return new RequestResultValue<ReloadProxiesResult>(false, e?.message || 'Network error');
    }
}
